import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OryIamConfig, type OryGetInput, type OryGetOutput, type OryExecuteOutput } from '../model';
import { Observable } from 'rxjs';
import type {
  AcceptOAuth2ConsentRequest,
  AcceptOAuth2LoginRequest,
  OAuth2LoginRequest,
  RejectOAuth2Request,
  OAuth2RedirectTo,
} from '@ory/client';

/**
 * OIDC API client for calling Lambda proxy functions.
 *
 * Calls AWS Lambda functions that act as a secure backend proxy to Ory Hydra admin APIs.
 * This prevents exposing admin API keys in the frontend.
 *
 * Lambda endpoints:
 * - POST /oauth/consent - Get consent request details
 * - POST /oauth/consent/accept - Accept consent request
 * - POST /oauth/consent/reject - Reject consent request
 * - POST /oauth/consent/revoke - Revoke consent sessions
 * - POST /oauth/login - Get login request details
 * - POST /oauth/login/accept - Accept login request
 * - POST /oauth/login/reject - Reject login request
 * - POST /oauth/login/revoke - Revoke login sessions
 * - POST /oauth/logout - Get logout request details
 * - POST /oauth/logout/accept - Accept logout request
 * - POST /oauth/logout/reject - Reject logout request
 *
 * All requests and responses use Ory's official types.
 */
@Injectable({
  providedIn: 'root'
})
export class OryOidcApi {

  readonly #httpClient = inject(HttpClient);
  readonly #config = inject(OryIamConfig).oidc;

  /**
   * Validates and returns the configured endpoint.
   * Throws descriptive error if OIDC is not properly configured.
   */
  get #endpoint(): string {
    if (!this.#config.oauthApiEndpoint?.trim()) {
      throw new Error(
        'OIDC is not configured. Please provide a valid oauthApiEndpoint in your OryIamConfig.'
      );
    }
    return this.#config.oauthApiEndpoint;
  }

  // Computed endpoint paths using readonly getters
  get #consentPath(): string {
    return `${this.#endpoint}${this.#config.pathConsent || '/oauth/consent'}`;
  }

  get #loginPath(): string {
    return `${this.#endpoint}${this.#config.pathLogin || '/oauth/login'}`;
  }

  get #logoutPath(): string {
    return `${this.#endpoint}${this.#config.pathLogout || '/oauth/logout'}`;
  }

  /**
   * Get OAuth2 consent request details.
   *
   * @param data - Consent challenge from URL query parameter
   * @returns Observable of consent request with client and scope details
   */
  getConsentRequest(data: OryGetInput<'oauth2_consent'>): Observable<OryGetOutput<'oauth2_consent'>> {
    return this.#httpClient.post<OryGetOutput<'oauth2_consent'>>(
      this.#consentPath,
      data
    );
  }

  /**
   * Accept OAuth2 consent request.
   *
   * Calls Lambda proxy endpoint which handles Hydra admin API authentication
   * and enriches the request with OIDC claims.
   *
   * IMPORTANT: `subject` is REQUIRED for Authorization Code flow.
   * - Subject identifies the user granting consent
   * - Required to enrich ID tokens with user claims (email, name, etc.)
   * - Always present in consent request after successful login
   *
   * @param consentChallenge - The consent challenge
   * @param acceptData - Accept consent request data
   * @param subject - The subject (user ID) from the consent request - REQUIRED
   * @returns Observable of OAuth2 redirect response
   */
  acceptConsentRequest(
    consentChallenge: string,
    acceptData: AcceptOAuth2ConsentRequest,
    subject: string
  ): Observable<OAuth2RedirectTo> {
    return this.#httpClient.post<OAuth2RedirectTo>(
      `${this.#consentPath}/accept`,
      { consentChallenge, acceptData, subject }
    );
  }

  /**
   * Reject OAuth2 consent request.
   *
   * Calls Lambda proxy endpoint which handles Hydra admin API authentication.
   *
   * @param consentChallenge - The consent challenge
   * @param rejectData - Reject request data
   * @returns Observable of OAuth2 redirect response
   */
  rejectConsentRequest(
    consentChallenge: string,
    rejectData: RejectOAuth2Request
  ): Observable<OAuth2RedirectTo> {
    return this.#httpClient.post<OAuth2RedirectTo>(
      `${this.#consentPath}/reject`,
      { consentChallenge, rejectData }
    );
  }

  /* ========================================
   * Login Request Methods
   * ========================================
   *
   * Handles OAuth2 login acceptance for prompt=none and skip flows.
   */

  /**
   * Get OAuth2 login request details.
   *
   * Called when user lands on login page with `?login_challenge=...`
   * Returns login request with skip flag, subject, and client details.
   *
   * @param loginChallenge - The login challenge from URL query parameter
   * @returns Observable of login request details
   */
  getLoginRequest(loginChallenge: string): Observable<OAuth2LoginRequest> {
    return this.#httpClient.post<OAuth2LoginRequest>(
      this.#loginPath,
      { loginChallenge }
    );
  }

  /**
   * Accepts the login request.
   *
   * @remarks
   * For skip=true flows (prompt=none), this accepts the login without
   * displaying UI, using the subject from the existing session.
   *
   * @param loginChallenge - The login challenge
   * @param acceptData - Accept data (subject, remember, ACR, etc.)
   * @returns Observable of redirect response (caller handles redirect)
   */
  acceptLoginRequest(
    loginChallenge: string,
    acceptData: AcceptOAuth2LoginRequest,
  ): Observable<OAuth2RedirectTo> {
    return this.#httpClient.post<OAuth2RedirectTo>(
      `${this.#loginPath}/accept`,
      { loginChallenge, acceptData }
    );
  }

  /**
   * Rejects the login request.
   *
   * @param loginChallenge - The login challenge
   * @param rejectData - Error code and description for rejection
   * @returns Observable of redirect response (caller handles redirect)
   */
  rejectLoginRequest(
    loginChallenge: string,
    rejectData: RejectOAuth2Request,
  ): Observable<OAuth2RedirectTo> {
    return this.#httpClient.post<OAuth2RedirectTo>(
      `${this.#loginPath}/reject`,
      { loginChallenge, rejectData }
    );
  }

  /**
   * Revokes all OAuth2 login sessions for a subject.
   *
   * @remarks
   * Terminates all active OAuth2 login sessions for a user. Used during
   * logout to clear remembered authentication decisions across Kratos and Hydra.
   *
   * Backend endpoint: POST /oauth/login/revoke
   * Hydra Admin API: DELETE /admin/oauth2/auth/sessions/login?subject={subject}
   *
   * Persistent login sessions (created with `remember=true`) enable skip flow
   * re-authentication. Revoking them forces full authentication on next OAuth flow.
   */
  revokeLoginSessions(subject: string): Observable<void> {
    return this.#httpClient.post<void>(
      `${this.#loginPath}/revoke`,
      { subject }
    );
  }

  /* ========================================
   * Logout Request Methods
   * ========================================
   *
   * Handles OIDC RP-Initiated Logout via Lambda proxy.
   */

  /**
   * Get OAuth2 logout request details.
   *
   * Called when user lands on logout page with `?logout_challenge=...`
   * Returns logout request with subject, SID, client, and rp_initiated flag.
   *
   * @param logoutChallenge - The logout challenge from URL query parameter
   * @returns Observable of logout request details
   */
  getLogoutRequest(logoutChallenge: string): Observable<OryGetOutput<'oauth2_logout'>> {
    return this.#httpClient.post<OryGetOutput<'oauth2_logout'>>(
      this.#logoutPath,
      { logoutChallenge }
    );
  }

  /**
   * Accepts the logout request.
   *
   * @remarks
   * Returns redirect_to URL containing the logout_verifier. The browser
   * MUST follow this redirect for Hydra to invalidate the session and
   * trigger front/back-channel logout notifications.
   *
   * @param logoutChallenge - The logout challenge
   * @returns Observable of redirect response with logout_verifier URL
   */
  acceptLogoutRequest(logoutChallenge: string): Observable<OryExecuteOutput<'oauth2_logout_accept'>> {
    return this.#httpClient.post<OryExecuteOutput<'oauth2_logout_accept'>>(
      `${this.#logoutPath}/accept`,
      { logoutChallenge }
    );
  }

  /**
   * Rejects the logout request.
   *
   * @param logoutChallenge - The logout challenge
   * @returns Observable that completes when rejection is processed
   */
  rejectLogoutRequest(logoutChallenge: string): Observable<OryExecuteOutput<'oauth2_logout_reject'>> {
    return this.#httpClient.post<OryExecuteOutput<'oauth2_logout_reject'>>(
      `${this.#logoutPath}/reject`,
      { logoutChallenge }
    );
  }

  /**
   * Revokes OAuth2 consent sessions for a subject.
   *
   * @remarks
   * When clientId is provided, revokes consent for that specific client only.
   * Otherwise revokes all consent sessions. This is an authorization
   * revocation API, separate from logout.
   *
   * Backend endpoint: POST /oauth/consent/revoke
   * Hydra Admin API: DELETE /admin/oauth2/auth/sessions/consent
   *
   * @param subject - User ID whose consent sessions to revoke
   * @param clientId - Optional OAuth client ID for per-client revocation
   */
  revokeConsentSessions(subject: string, clientId?: string): Observable<void> {
    return this.#httpClient.post<void>(
      `${this.#consentPath}/revoke`,
      clientId ? { subject, client: clientId } : { subject }
    );
  }
}
